import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { UserIcon, KeyIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const DemoLogin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const demoUsers = [
    {
      id: 'admin',
      name: 'Admin User',
      email: 'admin@crm.com',
      password: 'admin123',
      role: 'SuperAdmin',
      description: 'Full access to all features',
      color: 'bg-red-500 hover:bg-red-600'
    },
    {
      id: 'sales',
      name: 'Sales Manager',
      email: 'sales@crm.com',
      password: 'sales123',
      role: 'Sales',
      description: 'Lead management and sales features',
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      id: 'support',
      name: 'Support Agent',
      email: 'support@crm.com',
      password: 'support123',
      role: 'Support',
      description: 'Customer support and follow-ups',
      color: 'bg-green-500 hover:bg-green-600'
    }
  ];

  const handleDemoLogin = async (user) => {
    try {
      setLoading(true);
      setError('');
      
      const result = await login(user.email, user.password, 'superadmin');
      
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-2xl font-bold">CRM</span>
            </div>
          </div>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">
            Welcome to CRM Lead Management
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Choose a demo account to explore our comprehensive lead management system
          </p>
          
          {error && (
            <div className="max-w-md mx-auto mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
        </div>

        {/* Demo User Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {demoUsers.map((user) => (
            <div
              key={user.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200"
            >
              <div className={`${user.color} p-6 text-white`}>
                <div className="flex items-center justify-between mb-4">
                  <UserIcon className="h-8 w-8" />
                  <span className="text-sm font-medium bg-white bg-opacity-20 px-3 py-1 rounded-full">
                    {user.role}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-2">{user.name}</h3>
                <p className="text-sm opacity-90">{user.description}</p>
              </div>
              
              <div className="p-6">
                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-gray-600">
                    <UserIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm">{user.email}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <KeyIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm">Password: {user.password}</span>
                  </div>
                </div>
                
                <button
                  onClick={() => handleDemoLogin(user)}
                  disabled={loading}
                  className={`w-full ${user.color} text-white py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Logging in...</span>
                    </>
                  ) : (
                    <>
                      <span>Login as {user.role}</span>
                      <ArrowRightIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Features Overview */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            System Features
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <UserIcon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Lead Management</h3>
              <p className="text-sm text-gray-600">Comprehensive lead tracking and management</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <ArrowRightIcon className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Follow-ups</h3>
              <p className="text-sm text-gray-600">Automated follow-up scheduling and reminders</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <KeyIcon className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Analytics</h3>
              <p className="text-sm text-gray-600">Detailed reports and performance metrics</p>
            </div>
            <div className="text-center">
              <div className="bg-yellow-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <UserIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Team Management</h3>
              <p className="text-sm text-gray-600">Role-based access and team collaboration</p>
            </div>
          </div>
        </div>

        {/* Custom Login Option */}
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            Want to use your own account?
          </p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
          >
            <UserIcon className="h-5 w-5 mr-2" />
            Custom Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoLogin;
